import { type ComposableResult, compose } from './composable';
import type { Guid, localeValue } from './types';
import { apiUrl } from '@/config';

const breadcrumbUrl = apiUrl + '/breadcrumbs';
export function useBreadcrumbForPage(
  breadcrumbId?: number,
): ComposableResult<Breadcrumb | null | undefined> {
  return compose(getBreadcrumbForPage(breadcrumbId));
}

export function useBreadcrumbs(): ComposableResult<
  (Breadcrumb & { items: Breadcrumb[] }) | null | undefined
> {
  return compose(getBreadcrumbs());
}

export type Breadcrumb = {
  url?: string;
  id?: number;
  label: localeValue;
  parent?: Breadcrumb;
  children?: Breadcrumb[];
  homePageId?: Guid;
};

const getBreadcrumbForPage = async (
  breadcrumbId?: number,
): Promise<Breadcrumb | undefined> => {
  if (!breadcrumbId) {
    return undefined;
  }

  const response = await fetch(`${breadcrumbUrl}/item/${breadcrumbId}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok || response.status === 204) return undefined;
  return await response.json();
};

function addParentsToBreadcrumbs(
  breadcrumb: Breadcrumb,
  parent: Breadcrumb,
  items: Breadcrumb[],
): void {
  breadcrumb.parent = {
    label: parent.label,
    id: parent.id,
    parent: parent.parent,
  };
  items.push(breadcrumb);
  breadcrumb.children?.forEach((child) =>
    addParentsToBreadcrumbs(child, breadcrumb, items),
  );
}
const getBreadcrumbs = async (): Promise<
  (Breadcrumb & { items: Breadcrumb[] }) | undefined
> => {
  const response = await fetch(`${breadcrumbUrl}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (response.status === 204) return { label: { no: '' }, items: [] };
  if (!response.ok || response.status === 204) return undefined;
  const data: (Breadcrumb & { items: Breadcrumb[] }) | undefined =
    await response.json();
  if (!data) return;
  data.items = [];
  data.children?.forEach((breadcrumb) =>
    addParentsToBreadcrumbs(breadcrumb, data, data.items),
  );
  return data;
};

export const createBreadcrumb = async (
  breadcrumb: Omit<Breadcrumb, 'id'>,
): Promise<Breadcrumb> => {
  const response = await fetch(breadcrumbUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(breadcrumb),
  });
  if (!response.ok) throw new Error('error.create_failed');
  return await response.json();
};
export const updateBreadcrumb = async (
  breadcrumb: Breadcrumb,
): Promise<void> => {
  const response = await fetch(`${breadcrumbUrl}/${breadcrumb.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(breadcrumb),
  });
  if (!response.ok) throw new Error('error.update_failed');
};
export const deleteBreadcrumb = async (breadcrumbId: number): Promise<void> => {
  const response = await fetch(`${breadcrumbUrl}/${breadcrumbId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('error.delete_failed');
};
